import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, GripVertical } from 'lucide-react';
import { getTierMap, setServiceTier } from '@/api/services';
import { useAuth } from '@/auth/useAuth';
import { SERVICE_TIERS } from '@/lib/serviceTier';
import type { TierMapService } from '@/types/oscar';

const TIER_MAP_KEY = ['tier-map'] as const;

// Solid rail colour per tier (brand tokens), matching the tier badge meaning.
const railClass: Record<string, string> = {
  '0': 'bg-red', '1': 'bg-orange', '2': 'bg-navy', '3': 'bg-darkGrey', u: 'bg-darkGrey/60',
};

const assetLabel = (a: { Hostname: string | null; SerialNumber: string | null; AssetId: number }) =>
  a.Hostname || a.SerialNumber || `#${a.AssetId}`;

function escapeCsv(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** One CSV row per (service, asset); services with no assets still get a row. */
function buildTierCsv(services: TierMapService[]): string {
  const header = ['Tier', 'Service', 'Group', 'Asset', 'Category', 'Status'];
  const lines = [header.join(',')];
  for (const s of services) {
    const tier = s.Tier != null ? `Tier ${s.Tier}` : 'Untiered';
    if (s.Assets.length === 0) {
      lines.push([tier, s.Name, s.ServiceGroup ?? '', '', '', ''].map(escapeCsv).join(','));
    } else {
      for (const a of s.Assets) {
        lines.push([tier, s.Name, s.ServiceGroup ?? '', assetLabel(a), a.Category, a.LifecycleState].map(escapeCsv).join(','));
      }
    }
  }
  return lines.join('\r\n');
}

/**
 * Tier map (CMDB) — services grouped by criticality tier with the assets that
 * support them, for impact analysis and disaster-recovery planning. Shared
 * assets (supporting more than one service) are flagged so an outage's blast
 * radius is visible. Data from GET /api/cmdb/tier-map.
 */
export function TierMapPage() {
  const { canMutate } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: TIER_MAP_KEY, queryFn: ({ signal }) => getTierMap(signal) });
  const services = useMemo(() => query.data ?? [], [query.data]);

  // Drag-and-drop re-tiering (editors only).
  const [dragId, setDragId] = useState<number | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mTier = useMutation({
    mutationFn: ({ id, tier }: { id: number; tier: number | null }) => setServiceTier(id, tier),
    // Optimistic: move the card immediately, roll back on failure.
    onMutate: async ({ id, tier }) => {
      setError(null);
      await queryClient.cancelQueries({ queryKey: TIER_MAP_KEY });
      const prev = queryClient.getQueryData<TierMapService[]>(TIER_MAP_KEY);
      queryClient.setQueryData<TierMapService[]>(TIER_MAP_KEY, (old) =>
        old?.map((s) => (s.ServiceId === id ? { ...s, Tier: tier } : s)));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(TIER_MAP_KEY, ctx.prev);
      setError('Could not change the tier — please try again.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TIER_MAP_KEY });
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });

  const dropOnTier = (key: string) => {
    setOverKey(null);
    const id = dragId;
    setDragId(null);
    if (id == null) return;
    const target = key === 'u' ? null : Number(key);
    const svc = services.find((s) => s.ServiceId === id);
    if (!svc || (svc.Tier ?? null) === target) return; // no-op if unchanged
    mTier.mutate({ id, tier: target });
  };

  // Asset ids supporting more than one service — the shared/blast-radius set.
  const sharedAssetIds = useMemo(() => {
    const count = new Map<number, number>();
    for (const s of services) for (const a of s.Assets) count.set(a.AssetId, (count.get(a.AssetId) ?? 0) + 1);
    return new Set([...count].filter(([, n]) => n > 1).map(([id]) => id));
  }, [services]);

  // Tier bands 0..3 then Untiered. Editors see every band (so empty ones are
  // drop targets); read-only users only see bands that have services.
  const bands = useMemo(() => {
    const groups: { key: string; label: string; short: string; rail: string; items: TierMapService[] }[] = [];
    for (const t of SERVICE_TIERS) {
      groups.push({ key: String(t.tier), label: t.label, short: t.short, rail: railClass[String(t.tier)], items: services.filter((s) => s.Tier === t.tier) });
    }
    groups.push({ key: 'u', label: 'Untiered', short: '—', rail: railClass.u, items: services.filter((s) => s.Tier == null) });
    return groups.filter((g) => canMutate || g.items.length > 0);
  }, [services, canMutate]);

  const exportCsv = () => {
    const blob = new Blob([buildTierCsv(services)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oscar-service-tiers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section>
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Tier map</h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            Services grouped by criticality tier with the assets that support them — for impact analysis and
            disaster-recovery planning. ◆ marks a shared asset (supports more than one service).
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={services.length === 0}
          className="inline-flex shrink-0 items-center gap-2 rounded border border-border bg-white px-3 py-2 text-sm font-medium text-navy hover:bg-bg disabled:opacity-40"
        >
          <Download className="h-4 w-4" aria-hidden /> Export
        </button>
      </header>

      {/* Legend + DR restore order. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        {SERVICE_TIERS.map((t) => (
          <span key={t.tier} className={`rounded-full px-2 py-0.5 font-semibold ${t.badgeClass}`}>{t.label}</span>
        ))}
        <span className="ml-auto text-darkGrey/60">DR restore order: Tier 0 → Tier 3</span>
      </div>

      {canMutate && <p className="mb-2 text-xs text-darkGrey/60">Drag a service onto a tier to reclassify it. Drag to <span className="font-semibold">Untiered</span> to clear.</p>}
      {error && <div role="alert" className="mb-2 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>}

      {query.isLoading && <p className="text-sm text-darkGrey/60">Loading…</p>}
      {query.isError && <p className="text-sm text-red">Could not load the tier map.</p>}
      {!query.isLoading && !query.isError && services.length === 0 && (
        <p className="text-sm text-darkGrey/60">No services found.</p>
      )}

      <div className="space-y-2">
        {bands.map((band) => (
          <div
            key={band.key}
            onDragOver={canMutate ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overKey !== band.key) setOverKey(band.key); } : undefined}
            onDragLeave={canMutate ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverKey((k) => (k === band.key ? null : k)); } : undefined}
            onDrop={canMutate ? (e) => { e.preventDefault(); dropOnTier(band.key); } : undefined}
            className={`flex overflow-hidden rounded border bg-white shadow-sm transition-colors ${overKey === band.key ? 'border-orange ring-2 ring-orange/40' : 'border-border'}`}
          >
            <div className={`flex w-32 shrink-0 flex-col justify-center gap-0.5 px-3 py-3 text-white ${band.rail}`}>
              <span className="text-lg font-extrabold leading-none">{band.short}</span>
              <span className="text-[11px] font-semibold opacity-95">{band.label.replace(/^Tier \d · /, '')}</span>
              <span className="text-[10px] opacity-85">{band.items.length} {band.items.length === 1 ? 'service' : 'services'}</span>
            </div>
            <div className="flex flex-1 flex-wrap content-start gap-2 p-2.5">
              {band.items.length === 0 && (
                <p className="w-full px-1 py-3 text-center text-[11px] italic text-darkGrey/50">
                  {overKey === band.key ? 'Drop to set this tier' : 'No services — drag one here'}
                </p>
              )}
              {band.items.map((s) => (
                <div
                  key={s.ServiceId}
                  draggable={canMutate}
                  onDragStart={canMutate ? (e) => { e.dataTransfer.setData('text/plain', String(s.ServiceId)); e.dataTransfer.effectAllowed = 'move'; setDragId(s.ServiceId); } : undefined}
                  onDragEnd={canMutate ? () => { setDragId(null); setOverKey(null); } : undefined}
                  className={`w-full max-w-[15rem] flex-1 rounded border border-border bg-bg/40 p-2 ${canMutate ? 'cursor-move' : ''} ${dragId === s.ServiceId ? 'opacity-50' : ''}`}
                  style={{ minWidth: '11rem' }}
                >
                  <div className="flex items-start gap-1">
                    {canMutate && <GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-darkGrey/30" aria-hidden />}
                    <div className="text-sm font-semibold text-navy">{s.Name}</div>
                  </div>
                  <div className="mb-1.5 flex flex-wrap gap-1 text-[10px] text-darkGrey/60">
                    <span>{s.ServiceGroup ?? 'Ungrouped'}</span>
                    {s.EnvironmentCount > 0 && <span>· {s.EnvironmentCount} env{s.EnvironmentCount === 1 ? '' : 's'}</span>}
                  </div>
                  {s.Assets.length === 0 ? (
                    <p className="text-[11px] italic text-darkGrey/50">No assets linked</p>
                  ) : (
                    <ul className="flex flex-col gap-1">
                      {s.Assets.map((a) => (
                        <li key={a.AssetId}>
                          <Link
                            to={`/assets/${a.AssetId}`}
                            className={`block truncate rounded px-1.5 py-0.5 font-mono text-[10.5px] hover:underline ${sharedAssetIds.has(a.AssetId) ? 'bg-orange/10 font-semibold text-orange' : 'bg-white text-darkGrey/80'}`}
                            title={`${assetLabel(a)} · ${a.Category} · ${a.LifecycleState}${sharedAssetIds.has(a.AssetId) ? ' · shared' : ''}`}
                          >
                            {sharedAssetIds.has(a.AssetId) ? '◆ ' : ''}{assetLabel(a)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
